import { useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { CommonErrorType } from '../../common/enums.js';
import { fieldCantBeEmptyMsg, pleaseCheckEmailMsg, t } from '../../common/i18n/index.js';
import { MultipleEmails } from '../../common/react-hook-form/fields/index.js';
import { getPrimaryMdmId } from '../../common/utils/accountUtils';
import { EMAIL_REGEX } from '../../common/utils/emailUtils.js';
import { convertToErrorStringMap } from '../../common/utils/errorUtils.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { closeDialog } from '../../public/site/dialogStore.js';
import { initiateEmployeeOnBoarding } from '../../selfservice/actions/index.js';
import { CompanySelector } from '../CompanySelector/CompanySelector.js';
import { ConfirmationDialog } from '../ConfirmationDialog/index.js';
import { FileUpload } from '../FileUpload/FileUpload.js';
import { getUserAccounts } from '../Header/dynamic/headerFunctions.js';
import type { CommonError } from '../../common/types/errors.js';
import type { EmployeeOnBoardingPayload } from '../../selfservice/actions/index.js';

import './OnboardEmployeeDialog.scss';

export interface OnboardEmployeeSceneProps {
  errors?: CommonError[];
  isLoading?: boolean;
}

const validateAndInitiateOnboarding = (
  input: string | null | undefined,
  field: string,
  onInitiateEmployeeOnBoarding: (payload: EmployeeOnBoardingPayload, validationErrors?: CommonError[]) => void
) => {
  const recipientEmails = input ? input.split(/\r\n|\n|\r/).filter(Boolean) : [];
  const validationErrors: CommonError[] = [];
  if (!recipientEmails.length) {
    validationErrors.push({
      field,
      message: field === 'emails' ? t.VPVR(fieldCantBeEmptyMsg) : t.L1QB('The file could not be read or is empty.'),
      type: CommonErrorType.VALIDATION,
    });
  } else if (recipientEmails.filter(email => EMAIL_REGEX.test(email)).length !== recipientEmails.length) {
    validationErrors.push({
      field,
      message:
        field === 'emails'
          ? t.ROMH(pleaseCheckEmailMsg)
          : t.PCNO('The file contains one or more invalid email addresses.'),
      type: CommonErrorType.VALIDATION,
    });
  } else if (recipientEmails.length > 500) {
    validationErrors.push({
      field,
      message:
        field === 'emails'
          ? t.HYGU('This field can contain up to {} email addresses.', '500')
          : t.VJYS('The file can contain up to 500 email addresses.'),
      type: CommonErrorType.VALIDATION,
    });
  }
  onInitiateEmployeeOnBoarding({ recipientEmails }, validationErrors);
};

export const OnboardEmployeeDialog = ({ errors, isLoading }: OnboardEmployeeSceneProps) => {
  const { authenticatedUser } = useAuth();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const emailsRef = useRef<string | null>('');
  const getFilesRef = useRef<() => File[]>(undefined);

  const onInitiateEmployeeOnBoarding = (payload: EmployeeOnBoardingPayload, validationErrors?: CommonError[]) => {
    dispatch(initiateEmployeeOnBoarding(payload, validationErrors));
  };

  const methods = useForm<{ emails: string }>({ defaultValues: { emails: '' } });
  const emailsValue = methods.watch('emails');

  useEffect(() => {
    emailsRef.current = emailsValue ?? '';
  }, [emailsValue]);

  const dialogValidationErrors = errors
    ? convertToErrorStringMap(errors.filter(err => err.type === CommonErrorType.VALIDATION))
    : undefined;
  const otherErrors = errors ? errors.filter(err => err.type !== CommonErrorType.VALIDATION) : undefined;

  const fileUploadObj = 'fileUpload';
  return (
    <ConfirmationDialog
      header={t.NYPL('Send a link')}
      body={
        <>
          <p>
            {t.L78N('An employee can use the link to log into the device store and select a device.')}
            <br />
            {t.FHD7(
              `You will receive confirmation of the employee's device agreement, which you must review and approve.`
            )}
          </p>
          <p>
            {t.M248('You can provide more than one email address at a time or attach e.g. a')}
            <strong> {t.ZB19('csv or text file')}</strong>{' '}
            {t.MRGG('containing email addresses (max. 500). Please note that xlsx file extension is not accepted.')}
          </p>
          <a
            href="https://static.elisa.com/v2/image/2tqybbhjs47b/3EzvYxsNmUAOeJExZlSp7O/employee-onboarding-invitation-tempate.csv"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t.FUAJ('Download here csv-example for email addresses.')}
          </a>
          <FormProvider {...methods}>
            <form className={`of-onboard-employee ${dsClass.MARGIN_TOP_3}`}>
              <CompanySelector
                className={dsClass.MARGIN_BOTTOM_3}
                labelText={t.F7YS('Select the company for which you want to invite employers')}
                userAccounts={getUserAccounts(authenticatedUser)}
                initialMdmId={searchParams.get('mdmId') || getPrimaryMdmId(authenticatedUser)}
                onInputChange={(_, option) => {
                  searchParams.set('mdmId', option.accountMasterId!);
                  setSearchParams(searchParams, { replace: true });
                }}
              />
              <MultipleEmails />
              <FileUpload
                className="of-onboard-employee__file-upload"
                getFilesFn={getFiles => (getFilesRef.current = getFiles)}
                limit={1}
                validationError={dialogValidationErrors && dialogValidationErrors[fileUploadObj]}
                allowedFormat={['text/csv', 'text/rtf', 'text/plain', '', 'application/vnd.ms-excel']} // https://christianwood.net/csv-file-upload-validation/
                fileTypeValidationErrorMessage={t.QENF(
                  'Invalid file format. Please save your file either in .csv or .txt format.'
                )}
              />
            </form>
          </FormProvider>
        </>
      }
      isLoading={isLoading}
      onCloseDialog={() => closeDialog()}
      onConfirm={() => {
        const files = getFilesRef.current!();
        if (files.length === 1) {
          const fileReader = new FileReader();
          fileReader.onloadend = () => {
            validateAndInitiateOnboarding(
              fileReader.result as string | null,
              fileUploadObj,
              onInitiateEmployeeOnBoarding
            );
          };
          fileReader.readAsText(files[0]);
        } else {
          validateAndInitiateOnboarding(emailsRef.current, 'emails', onInitiateEmployeeOnBoarding);
        }
      }}
      errors={otherErrors}
    />
  );
};
